import { ClearRounded } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import { attributeValueAssociationName, constraints, inputTypes } from "data/pimData";
import { useEffect, useMemo, useState } from "react";

const fieldSx = {
	"& .MuiOutlinedInput-root": {
		borderRadius: 2,
		backgroundColor: "background.paper",
	},
};

const RenderInputFields = ({ columns, setData, data }) => {
	return (
		<Box
			display="grid"
			gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
			columnGap={2}
			rowGap={2}
			width="100%">
			{Object.keys(columns).map((columnName) => {
				const column = columns[columnName];
				const value = data?.[column.key] ?? "";
				const isReadOnly = column.input_type === inputTypes.autoGenerate;
				const isSelect = ![inputTypes.enter, inputTypes.autoGenerate].includes(column.input_type);

				if (isSelect) {
					const options = column?.[attributeValueAssociationName]?.list || [];
					return (
						<TextField
							key={column.key}
							select
							fullWidth
							size="small"
							label={column.name}
							value={value}
							onChange={(event) => setData({ ...data, [column.key]: event.target.value })}
							required={column.type?.[constraints.notNull]}
							sx={fieldSx}>
							{options.map((option) => (
								<MenuItem key={option.key || option.value || option.name} value={option.key || option.value || option.name}>
									{option.name || option.label || option.value}
								</MenuItem>
							))}
						</TextField>
					);
				}

				return (
					<TextField
						key={column.key}
						fullWidth
						size="small"
						label={column.name}
						value={value}
						onChange={(event) => setData({ ...data, [column.key]: event.target.value })}
						required={column.type?.[constraints.notNull]}
						disabled={isReadOnly}
						type={column.type?.datatype === "number" ? "number" : "text"}
						InputProps={{
							readOnly: isReadOnly,
						}}
						sx={{
							...fieldSx,
							"& .MuiOutlinedInput-root": {
								...fieldSx["& .MuiOutlinedInput-root"],
								backgroundColor: isReadOnly ? grey[100] : "background.paper",
							},
						}}
					/>
				);
			})}
		</Box>
	);
};

// Edit Model
const EditModal = ({ modalOpen, setModalOpen, selectedStore, columns }) => {
	const handleCloseModal = () => {
		setModalOpen(false);
	};

	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();

	const [selectedStoreData, setSelectedStoreData] = useState({});
	const [originalData, setOriginalData] = useState({});
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (selectedStore) {
			const copy = { ...selectedStore };
			setSelectedStoreData(copy);
			setOriginalData(copy);
		}
	}, [selectedStore]);

	const isDirty = useMemo(() => {
		return JSON.stringify(selectedStoreData) !== JSON.stringify(originalData);
	}, [selectedStoreData, originalData]);

	const handleUpdate = async () => {
		if (!selectedStoreData?.site_code) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Missing store identifier please try with upload option",
				severity: "warning",
			});
			return;
		}

		// Build update payload (exclude auto-generated fields)
		const updates = {};
		Object.keys(columns).forEach((col) => {
			const column = columns[col];
			const key = column.key;

			if (column.input_type !== inputTypes.autoGenerate && selectedStoreData[key] !== originalData[key]) {
				updates[key] = selectedStoreData[key];
			}
		});

		if (Object.keys(updates).length === 0) {
			handleCloseModal();
			return;
		}

		const payload = {
			site_code: selectedStoreData.site_code, // WHERE condition
			updates,
		};

		try {
			setLoading(true);

			await axios.put("/api/pos_reco/update_store_data", payload);

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Store updated successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});

			handleCloseModal();
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable to update store",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={modalOpen} onClose={handleCloseModal} scroll="paper" maxWidth="lg" fullWidth>
			<Box display="flex" justifyContent="space-between" px={2.5} pt={2} alignItems="flex-start">
				<Box>
					<DialogTitle sx={{ p: 0 }}>Edit Store</DialogTitle>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Review store identifiers and update editable POS details below.
					</Typography>
				</Box>

				<IconButton sx={{ width: 42, height: 42, marginLeft: "auto" }} onClick={handleCloseModal}>
					<ClearRounded sx={{ transform: "scale(1.3)" }} />
				</IconButton>
			</Box>

			<DialogContent dividers sx={{ px: 2.5, py: 2 }}>
				<Box
					sx={{
						mb: 2,
						p: 1.5,
						borderRadius: 2,
						bgcolor: grey[50],
						border: "1px solid",
						borderColor: "divider",
					}}>
					<Typography variant="caption" color="text.secondary" fontWeight={600}>
						Store Code
					</Typography>
					<Typography variant="body1" fontWeight={600}>
						{selectedStoreData?.site_code || "-"}
					</Typography>
				</Box>

				<RenderInputFields data={selectedStoreData} setData={setSelectedStoreData} columns={columns} />
			</DialogContent>

			<DialogActions sx={{ px: 2.5, py: 2 }}>
				<Button variant="outlined" onClick={handleCloseModal}>
					Cancel
				</Button>

				<Button
					variant="contained"
					onClick={handleUpdate}
					startIcon={loading ? <CircularProgress color="inherit" size={20} /> : null}
					disabled={!isDirty || loading}>
					{loading ? "Saving..." : "Save Changes"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EditModal;
